"use client";

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { deviceDetector, getItem, getTypeAppOrPc, setItem } from "utils";
import { useSnapshot } from "valtio/react";
import { useParams } from "next/navigation";
import { OspToast } from "@ospweb/sdk-ui";
import {
  AppRatingSuggestionContent,
  type AppRatingSuggestionContentMode,
} from "./AppRatingSuggestionContent";
import Translations from "@translate/components/Home/AppRating/AppRatingSuggestionModalContainer";
import { I18nUtil } from "@utils/i18n";
import EdgenDrawer from "@components/Drawer";
import { useBridge } from "@/src/lib/bridge";
import { MessageType } from "@/src/lib/bridge/types";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { rootStore } from "@store/rootStore";
import { useCredits } from "@hooks/useCredits";
import { useGlobalAppModalLimit } from "@hooks/useGlobalAppModalLimit";
import { usePostTaskList } from "@/src/hooks/usePostTaskList";
import { ModalStore } from "@store/modules/modal";

const I18n = I18nUtil.createScoped(Translations);

const FIFTEEN_DAYS_MS = 15 * 24 * 60 * 60 * 1000;

const GLOBAL_APP_MODAL_KEY_APP_RATING = "app-rating";

/**
 * 是否满足「15 天内未展示过」可再次展示
 */
function canShowBy15Days(): boolean {
  const last = getItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_LAST_SHOWN);
  if (last == null) {
    return true;
  }
  return Date.now() - Number(last) >= FIFTEEN_DAYS_MS;
}

// 通知弹窗是否在5分钟内打开过
function isNotificationModalOpenedIn5Minutes(): boolean {
  const last = getItem(
    LOCAL_STORAGE_KEYS.TURN_ON_NOTIFICATION_MODAL_LAST_SHOWN,
  );
  if (last == null) {
    return false;
  }
  return Date.now() - Number(last) < 5 * 60 * 1000;
}

/**
 * 记录本次展示时间
 */
function markShown(): void {
  setItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_LAST_SHOWN, Date.now());
}

/**
 * App 内评分/建议弹窗 - 容器层
 * 仅 APP 环境渲染；使用 EdgenDrawer，管理 visible 与 contentMode；
 * 通过 pubsub.emit(pubSubKeyMap.OPEN_APP_RATING_SUGGESTION_MODAL) 打开弹窗。
 */
export const AppRatingSuggestionModalContainer: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [contentMode, setContentMode] =
    useState<AppRatingSuggestionContentMode>("rating");
  const [text, setText] = useState("");
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { memberUserData, status } = useCredits();
  const { locale } = useParams<{ locale: string }>();
  const { appRatingModalVisible } = useSnapshot(ModalStore);
  const { postTaskList: allTasks } = usePostTaskList({
    position: "list",
    channel: getTypeAppOrPc(),
    locale,
  });

  const { request } = useBridge();

  const closeModalRef = useRef<() => void>(() => {});
  const { scheduleOpen, cancelPendingOpen } = useGlobalAppModalLimit(
    GLOBAL_APP_MODAL_KEY_APP_RATING,
    {
      hideInAppChatModal: true,
      onRemoteClaim: () => closeModalRef.current(),
    },
  );
  const cancelPendingOpenRef = useRef(cancelPendingOpen);
  cancelPendingOpenRef.current = cancelPendingOpen;

  const closeModal = useCallback(() => {
    cancelPendingOpenRef.current();
    setVisible(false);
    setContentMode("rating");
    ModalStore.appRatingModalVisible = false;
  }, []);
  closeModalRef.current = closeModal;

  const openModal = useCallback(() => {
    setContentMode("rating");
    const rated = getItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_RATED);
    // 不是APP不显示 未登录不显示 已评分不显示 15天内展示过 不显示
    if (
      !deviceDetector.isApp() ||
      !isLoggedIn ||
      rated ||
      !canShowBy15Days() ||
      isNotificationModalOpenedIn5Minutes()
    ) {
      return;
    }
    if (
      ModalStore.appVersionUpdateModalVisible ||
      ModalStore.turnOnNotificationModalVisible ||
      ModalStore.lowCreditModalVisible ||
      ModalStore.trackerWelcomeModalVisible
    ) {
      return;
    }
    ModalStore.appRatingModalVisible = true;
    markShown();
    setVisible(true);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!appRatingModalVisible && visible) {
      closeModal();
    }
  }, [appRatingModalVisible, visible, closeModal]);

  // 判断用户在会员有效期内
  const _isTodayPurchased = useMemo(() => {
    if (status !== "success") {
      return false;
    }
    return !memberUserData?.isFree;
  }, [memberUserData?.isFree, status]);

  // 判断是否消耗了200credit
  const _isConsumed200Credit = useMemo(() => {
    return (memberUserData?.credit?.used ?? 0) >= 200;
  }, [memberUserData?.credit?.used]);

  // 判断用户完成了所有的新手任务
  const _isCompletedAllNewbieTasks = useMemo(() => {
    const onboardingMissions = allTasks?.find(
      (task) => task.code === "onboarding_missions",
    );
    return (
      onboardingMissions?.status === 1 && onboardingMissions?.rewardStatus === 1
    );
  }, [allTasks]);

  useEffect(() => {
    if (
      _isTodayPurchased ||
      _isConsumed200Credit ||
      _isCompletedAllNewbieTasks
    ) {
      scheduleOpen(10000, openModal);
    }
  }, [
    scheduleOpen,
    openModal,
    _isTodayPurchased,
    _isConsumed200Credit,
    _isCompletedAllNewbieTasks,
  ]);

  const handleRateUs = useCallback(async () => {
    cancelPendingOpenRef.current();
    // 用户已进行评分
    await request(MessageType.REQUEST_APP_RATE);
    setItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_RATED, true);
    closeModal();
  }, [request, closeModal]);

  const handleOfferSuggestion = useCallback(() => {
    setContentMode("suggestion");
  }, []);

  const handleSubmit = useCallback(() => {
    // 用户的吐槽通过日志上报到阿里云 阿里云定期进行导出到Google 表格
    closeModal();
    OspToast.success({
      content: I18n.t("Submission successful!"),
    });
  }, [closeModal, text]);

  if (!deviceDetector.isApp()) {
    return null;
  }

  return (
    <EdgenDrawer
      isOpen={visible}
      onClose={closeModal}
      placement="bottom"
      width="100%"
      contentClassName="pb-0 no-scrollbar"
      titleClassName="hidden"
      isMask={false}
      maskClosable={false}
      styles={{
        wrapper: {
          minHeight: "370px",
          height: "auto",
        },
        body: {
          padding: "24px 24px var(--safe-area-inset-bottom) 24px",
          scrollbarWidth: "none",
          minHeight: "370px",
        },
      }}
    >
      <AppRatingSuggestionContent
        mode={contentMode}
        onRateUs={handleRateUs}
        onOfferSuggestion={handleOfferSuggestion}
        onSubmit={handleSubmit}
        onMaybeLater={closeModal}
        text={text}
        setText={setText}
      />
    </EdgenDrawer>
  );
};
